import { Subscription, combineLatest } from 'rxjs';
import { depth$, estimate$, height$, width$, subtotal$ } from '../lib/state';
import Logger from '../../js/lib/logger';
import StorageService from '../lib/storage.service';
import { Measurement } from '../lib/constants';
import { EstimateData, PricingData } from '../lib/types';
import { getFancySingleDimString, money } from '../lib/util.functions';

class CustomSizeForm extends HTMLElement {
  #NAME = 'CustomSizeForm';
  #subject$ = combineLatest({
    width: width$,
    height: height$,
    depth: depth$,
    subtotal: subtotal$,
    estimate: estimate$,
  });
  #sub?: Subscription;
  #pricingData: PricingData;
  #priceEL: HTMLElement;
  #variantID?: string;
  #customIDs: string[];
  #formInputs: {
    refs: HTMLInputElement;
    quantities: HTMLInputElement;
    prices: HTMLInputElement;
    width: HTMLInputElement;
    height: HTMLInputElement;
    depth: HTMLInputElement;
  }

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    this.#pricingData = StorageService.get('pricingData');
    this.#pricingData.trayVariants.sort((a,b) => a.price - b.price);
    this.#customIDs = this.#pricingData.trayVariants.filter(v => v.evaluations).map(v => v.id);
    //
    if (StorageService.get('inserts')) {
      StorageService.clear();
      StorageService.save('pricingData', this.#pricingData, true);
      width$.next(Measurement.DEFAULT_WIDTH);
      height$.next(Measurement.MIN_HEIGHT);
      depth$.next(Measurement.DEFAULT_DEPTH);
    }
    //
    this.#priceEL = document.querySelector('[data-price-text]') as HTMLElement;
    this.#formInputs = {
      refs: document.querySelector('input[name="properties[_component_reference]"]') as HTMLInputElement,
      quantities: document.querySelector('input[name="properties[_component_quantities]"]') as HTMLInputElement,
      prices: document.querySelector('input[name="properties[_component_prices]"]') as HTMLInputElement,
      width: document.querySelector('input[name="properties[width]"]') as HTMLInputElement,
      height: document.querySelector('input[name="properties[height]"]') as HTMLInputElement,
      depth: document.querySelector('input[name="properties[depth]"]') as HTMLInputElement,
    }
  }

  connectedCallback(): void {
    Logger.info(`${this.#NAME} > connectedCallback`);
    //
    const colorInput = document.querySelector('[data-option="Color"]:has(input:checked)') as HTMLElement;
    let color = 'Black';
    if (colorInput) color = colorInput.dataset.value as string;
    //
    this.#sub = this.#subject$.subscribe((dta) => {
      console.log(dta);
      //
      const total = dta.estimate.otherCharges.map(e => e.price).reduce((a,b) => a + b, dta.estimate.tray.price);
      if (total !== dta.subtotal) {
        subtotal$.next(total);
        return;
      }
      //
      const estimateData: EstimateData = { ...dta.estimate, otherCharges: [] };
      // eval if other charges apply
      this.#pricingData.measurementChargeVariants.forEach(obj => {
        const addCharge = obj.evaluations?.map(e => dta[e.type] >= e.equalsOrMoreThan && dta[e.type] < e.lessThan)
                                        .reduce((a,b) => a ? a : b) || false;
        if (addCharge) {
          estimateData.otherCharges.push({
            id: obj.id,
            title: obj.title,
            price: obj.price
          })
        }
      })
      if (JSON.stringify(dta.estimate) !== JSON.stringify(estimateData)) {
        estimate$.next(estimateData);
        return;
      }
      //
      const filteredVariants = this.#pricingData.trayVariants.filter(obj => {
        return obj.evaluations?.length ? obj.evaluations?.map(e => dta[e.type] >= e.equalsOrMoreThan && dta[e.type] < e.lessThan)
                                        .reduce((a,b) => a ? a : b) : false;
      });
      //
      console.log('filteredVariants', filteredVariants);
      let trayVariant = filteredVariants[filteredVariants.length - 1];
      const size = trayVariant.title.split('/').map(v => v.trim())[1];
      if (color) {
        /* console.log('size: ', size, 'color: ', color); */
        const variant = filteredVariants.find(obj => obj.color === color && obj.title.includes(size));
        if (variant){
          trayVariant = variant;
          estimateData.tray.title += ` (${variant.title})`;
        }
      }
      const searchParams = new URLSearchParams(window.location.search);
      const curVariant = searchParams.get('variant') as string;
      /* console.log('curVariant: ', curVariant, 'customIDs: ', this.#customIDs); */
      if (this.#customIDs.includes(curVariant)) {
        console.log('is custom variant!', dta.subtotal);
        this.#priceEL.textContent = money(dta.subtotal);
        if (trayVariant.id !== this.#variantID) {
          /* searchParams.set('variant', trayVariant.id)
          window.location.search = searchParams.toString(); */
          (document.querySelector(`[data-option="Size"] input[value="${size}"]`) as HTMLInputElement)?.click();
        }
        //
        this.#formInputs.refs.value = JSON.stringify(estimateData.otherCharges.map(v => v.id));
        this.#formInputs.quantities.value = JSON.stringify(estimateData.otherCharges.map(v => 1));
        this.#formInputs.prices.value = JSON.stringify(estimateData.otherCharges.map(v => v.price));
        this.#formInputs.refs.disabled = !estimateData.otherCharges.length;
        this.#formInputs.quantities.disabled = !estimateData.otherCharges.length;
        this.#formInputs.prices.disabled = !estimateData.otherCharges.length;
        this.#formInputs.width.value = getFancySingleDimString( dta.width );
        this.#formInputs.height.value = getFancySingleDimString( dta.height );
        this.#formInputs.depth.value = getFancySingleDimString( dta.depth );
        this.#formInputs.width.disabled = false;
        this.#formInputs.height.disabled = false;
        this.#formInputs.depth.disabled = false;
        // save
        StorageService.save('width', dta.width, true);
        StorageService.save('height', dta.height, true);
        StorageService.save('depth', dta.depth, true);
      } else {
        this.#formInputs.refs.value = '[]';
        this.#formInputs.quantities.value = '[]';
        this.#formInputs.prices.value = '[]';
        this.#formInputs.refs.disabled = true;
        this.#formInputs.quantities.disabled = true;
        this.#formInputs.prices.disabled = true;
        this.#formInputs.width.value = '';
        this.#formInputs.height.value = '';
        this.#formInputs.depth.value = '';
        this.#formInputs.width.disabled = true;
        this.#formInputs.height.disabled = true;
        this.#formInputs.depth.disabled = true;
        const revertPrice = this.#pricingData.trayVariants.find(v => v.id === curVariant)?.price;
        console.log('is NOT custom variant!', revertPrice);
        if (revertPrice) this.#priceEL.textContent = money(revertPrice);
      }
    });
  }

  disconnectedCallback(): void {
    this.#sub?.unsubscribe();
  }

  static get observedAttributes(): string[] {
    return ['variant-id'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    Logger.info(`${this.#NAME} > attributeChangedCallback > name: ${name} > value: ${newValue}`);
    if (name === 'variant-id') {
      console.log(this.#pricingData)
      const est = { ...estimate$.value };
      const tray = this.#pricingData.trayVariants.find(v => v.id === newValue);
      if (tray) {
        this.#variantID = newValue;
        est.tray = {...tray, title: 'Base Price' };
        estimate$.next(est);
      }
    }
  }
}

customElements.define('custom-size-form', CustomSizeForm);